import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Plus, Search, Trash2, Edit3, X, Check } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { Skeleton } from '@/components/ui/skeleton.js';

interface SeoItem {
  id: string;
  project_id?: string | null;
  title: string;
  item?: string;
  url?: string | null;
  target_url?: string | null;
  type?: string;
  category?: string;
  priority: string;
  status: string;
  notes?: string | null;
}

export function SeoPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const canManage = isOwner || user?.role === 'manager';

  const [items, setItems] = useState<SeoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<string>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    url: '',
    type: 'on_page',
    priority: 'medium',
    notes: '',
  });

  const [editingItem, setEditingItem] = useState<SeoItem | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    url: '',
    type: 'on_page',
    priority: 'medium',
    notes: '',
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Delete Confirmation State
  const [deleteSeoTarget, setDeleteSeoTarget] = useState<{ id: string; name: string } | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!editingItem && !showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingItem(null);
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingItem, showModal]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchItems = async () => {
    try {
      const res = await api.get<{ data: SeoItem[] }>('/api/v1/seo');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setItems(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleToggleStatus = async (item: SeoItem) => {
    const nextStatus = item.status === 'completed' ? 'pending' : 'completed';
    try {
      await api.patch(`/api/v1/seo/${item.id}`, { status: nextStatus });
      fetchItems();
    } catch (e: any) {
      alert(e.message || 'Failed to update SEO item');
    }
  };

  const handleCreateItem = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/seo', {
        ...newItem,
        item: newItem.title,
        target_url: newItem.url,
      });
      setShowModal(false);
      setNewItem({ title: '', url: '', type: 'on_page', priority: 'medium', notes: '' });
      showToast('SEO item added successfully!');
      fetchItems();
    } catch (e: any) {
      alert(e.message || 'Failed to create SEO item');
    }
  };

  const handleOpenEdit = (item: SeoItem) => {
    setEditingItem(item);
    setEditForm({
      title: item.title || item.item || '',
      url: item.url || (item as any).target_url || '',
      type: item.type || item.category || 'on_page',
      priority: item.priority || 'medium',
      notes: item.notes || '',
    });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    try {
      await api.patch(`/api/v1/seo/${editingItem.id}`, {
        ...editForm,
        item: editForm.title,
        target_url: editForm.url,
      });
      setEditingItem(null);
      showToast('SEO item updated successfully!');
      fetchItems();
    } catch (e: any) {
      alert(e.message || 'Failed to update SEO item');
    }
  };

  const handleDeleteItem = (id: string, title?: string) => {
    setDeleteSeoTarget({ id, name: title || 'this SEO item' });
  };

  const confirmDeleteSeo = async () => {
    if (!deleteSeoTarget) return;
    const { id } = deleteSeoTarget;
    try {
      await api.delete(`/api/v1/seo/${id}`);
      setDeleteSeoTarget(null);
      showToast('SEO item deleted.');
      fetchItems();
    } catch (e: any) {
      alert(e.message || 'Failed to delete SEO item');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    const itemTitle = item.title || item.item || '';
    const itemType = item.type || item.category || '';
    const matchesSearch = q
      ? itemTitle.toLowerCase().includes(q) ||
        (item.url && item.url.toLowerCase().includes(q)) ||
        itemType.toLowerCase().includes(q)
      : true;
    const matchesType = typeFilter ? itemType === typeFilter : true;
    const matchesStatus = statusFilter ? item.status === statusFilter : true;
    return matchesSearch && matchesType && matchesStatus;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField] || (sortField === 'title' ? a.item : '') || '';
    let bVal = b[sortField] || (sortField === 'title' ? b.item : '') || '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">SEO Checklists</h1>
          <p className="helper-text mt-1">Technical SEO deliverables, Core Web Vitals, and on-page auditing.</p>
        </div>
        {canManage && (
          <Button
            onClick={() => setShowModal(true)}
            className="bg-brand-teal hover:bg-brand-teal-action text-white flex items-center space-x-1 font-semibold text-xs"
          >
            <Plus className="h-4 w-4" />
            <span>New SEO Item</span>
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-3 rounded-lg border border-slate-200">
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search SEO items..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          >
            <option value="">All Types</option>
            <option value="on_page">On-Page</option>
            <option value="technical">Technical</option>
            <option value="content">Content</option>
            <option value="link">Link</option>
            <option value="local">Local</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="flex items-center space-x-4 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort by:</span>
          <SortButton label="Title" field="title" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Priority" field="priority" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Status" field="status" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {/* SEO Items Card */}
      <Card>
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800">Checklist Items</CardTitle>
          <Badge variant="secondary">{sorted.length} items</Badge>
        </CardHeader>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-3 flex items-center justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-4 w-52" />
                      <Skeleton className="h-4 w-16 rounded-full" />
                      <Skeleton className="h-4 w-20 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-40" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-7 w-7 rounded-md" />
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No SEO items found matching criteria.</div>
          ) : (
            paginated.map((item) => {
              const itemTitle = item.title || item.item || 'SEO Audit Item';
              return (
                <div
                  key={item.id}
                  className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div
                    onClick={() => handleToggleStatus(item)}
                    className="flex items-center space-x-3 cursor-pointer flex-1"
                  >
                    <input
                      type="checkbox"
                      checked={item.status === 'completed'}
                      onChange={() => {}}
                      className="accent-brand-teal h-4 w-4 rounded"
                    />
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-semibold ${item.status === 'completed' ? 'line-through text-slate-400' : 'text-brand-navy'}`}>
                          {itemTitle}
                        </span>
                        <Badge variant="secondary" className="capitalize text-[10px]">
                          {String(item.type || item.category || 'SEO').replace('_', ' ')}
                        </Badge>
                        <Badge variant={(item.priority || 'medium') as any} className="uppercase text-[9px]">
                          {item.priority || 'medium'}
                        </Badge>
                      </div>
                      {item.url && <p className="text-xs text-brand-teal truncate max-w-sm">{item.url}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={(item.status || 'pending') as any} className="capitalize text-xs">
                      {String(item.status || 'pending').replace('_', ' ')}
                    </Badge>
                    {canManage && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleOpenEdit(item)}
                        className="h-7 w-7 text-slate-400 hover:text-brand-navy"
                        title="Edit Item"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {isOwner && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteItem(item.id, itemTitle)}
                        className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50"
                        title="Delete Item"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <Pagination
          totalItems={sorted.length}
          currentPage={validCurrentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </Card>

      {/* Add Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Add SEO Checklist Item</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateItem} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Title *</label>
                <Input
                  required
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="e.g. Optimize Core Web Vitals LCP"
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Target URL</label>
                <Input
                  value={newItem.url}
                  onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                  placeholder="https://example.com/page"
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Type</label>
                <select
                  value={newItem.type}
                  onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                >
                  <option value="on_page">On-Page</option>
                  <option value="technical">Technical</option>
                  <option value="content">Content</option>
                  <option value="link">Link</option>
                  <option value="local">Local</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Priority</label>
                <select
                  value={newItem.priority}
                  onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Add Item
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setEditingItem(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Edit SEO Item</h2>
              <button onClick={() => setEditingItem(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Title *</label>
                <Input
                  required
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Target URL</label>
                <Input
                  value={editForm.url}
                  onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                  className="mt-1 h-9 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Type</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                >
                  <option value="on_page">On-Page</option>
                  <option value="technical">Technical</option>
                  <option value="content">Content</option>
                  <option value="link">Link</option>
                  <option value="local">Local</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Priority</label>
                <select
                  value={editForm.priority}
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setEditingItem(null)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!deleteSeoTarget}
        itemType="SEO Item"
        itemName={deleteSeoTarget?.name}
        onClose={() => setDeleteSeoTarget(null)}
        onConfirm={confirmDeleteSeo}
      />
    </div>
  );
}
